export const CUSTOMER_ACTION_TYPES = Object.freeze({
  ORIGINAL_DOWNLOAD: "ORIGINAL_DOWNLOAD",
  THEME_SELECTION: "THEME_SELECTION",
  PRODUCT_PHOTO_SELECTION: "PRODUCT_PHOTO_SELECTION",
});

export const CUSTOMER_ACTION_PRIORITIES = Object.freeze({
  URGENT: "urgent",
  HIGH: "high",
  MEDIUM: "medium",
  NORMAL: "normal",
});

export const CUSTOMER_ACTION_SNOOZE_STORAGE_PREFIX = "luvmom.customerActionSnoozes.v1";
export const CUSTOMER_ACTION_COUNT_EVENT = "luvmom:customer-action-count";
export const CUSTOMER_ACTION_COUNT_STORAGE_PREFIX = "luvmom.customerActionCount.v1";
export const CUSTOMER_ACTION_COMPLETED_STORAGE_PREFIX = "luvmom.customerActionCompleted.v1";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_SNOOZE_MS = DAY_IN_MS;

const ACTION_TYPE_SORT_ORDER = Object.freeze({
  [CUSTOMER_ACTION_TYPES.ORIGINAL_DOWNLOAD]: 0,
  [CUSTOMER_ACTION_TYPES.THEME_SELECTION]: 1,
  [CUSTOMER_ACTION_TYPES.PRODUCT_PHOTO_SELECTION]: 2,
});

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function firstFinite(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) return numberValue;
  }
  return 0;
}

function firstNonEmptyArray(...values) {
  return values.find((value) => Array.isArray(value) && value.length > 0) || [];
}

function firstValidDateEntry(entries) {
  return entries.find((entry) => parseDate(entry.value)) || null;
}

function normalizeRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.result)) return value.result;
  if (Array.isArray(value?.data?.result)) return value.data.result;
  return [];
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  if (typeof value === "string") {
    const dateOnlyMatch = value.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const dateOnly = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(dateOnly.getTime()) ? null : dateOnly;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(value) {
  const date = parseDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function toIsoString(value) {
  const date = parseDate(value);
  return date ? date.toISOString() : null;
}

function addDays(value, numberOfDays) {
  const date = startOfDay(value) || startOfDay(new Date());
  date.setDate(date.getDate() + numberOfDays);
  return date;
}

/**
 * Adds calendar months without overflowing the day (August 31 + 6 months = February 28/29).
 */
export function addMonthsClamped(value, numberOfMonths) {
  const date = parseDate(value);
  if (!date) return null;

  const originalDay = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + numberOfMonths);
  const lastDayOfTargetMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  return date;
}

export function getRemainingDays(dueAt, now = new Date()) {
  const dueDate = startOfDay(dueAt);
  const today = startOfDay(now);
  if (!dueDate || !today) return null;
  return Math.round((dueDate.getTime() - today.getTime()) / DAY_IN_MS);
}

export function getActionDdayLabel(remainingDays) {
  if (!Number.isFinite(remainingDays)) return "";
  if (remainingDays < 0) return "기한 지남";
  if (remainingDays === 0) return "D-Day";
  return `D-${remainingDays}`;
}

export function getActionPriority(remainingDays) {
  if (remainingDays === null || !Number.isFinite(remainingDays)) {
    return { priority: CUSTOMER_ACTION_PRIORITIES.NORMAL, priorityRank: 3 };
  }
  if (remainingDays <= 3) {
    return { priority: CUSTOMER_ACTION_PRIORITIES.URGENT, priorityRank: 0 };
  }
  if (remainingDays <= 7) {
    return { priority: CUSTOMER_ACTION_PRIORITIES.HIGH, priorityRank: 1 };
  }
  if (remainingDays <= 14) {
    return { priority: CUSTOMER_ACTION_PRIORITIES.MEDIUM, priorityRank: 2 };
  }
  return { priority: CUSTOMER_ACTION_PRIORITIES.NORMAL, priorityRank: 3 };
}

export function formatActionDate(value) {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

export function formatFileSize(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const amount = bytes / (1024 ** unitIndex);
  return `${amount >= 10 || unitIndex === 0 ? Math.round(amount) : amount.toFixed(1)}${units[unitIndex]}`;
}

function withActionTiming(item, now) {
  const remainingDays = getRemainingDays(item.dueAt, now);
  const { priority, priorityRank } = getActionPriority(remainingDays);
  return {
    ...item,
    remainingDays,
    ddayLabel: getActionDdayLabel(remainingDays),
    priority,
    priorityRank,
  };
}

function appendQuery(path, params) {
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
  return query ? `${path}${path.includes("?") ? "&" : "?"}${query}` : path;
}

function resolveOriginalDownloadExpiry(row) {
  const detail = row?.detail || {};
  const explicitExpiry = firstValidDateEntry([
    { value: row?.download_expires_at, source: "download_expires_at" },
    { value: detail.download_expires_at, source: "download_expires_at" },
    { value: row?.expires_at, source: "expires_at" },
    { value: detail.expires_at, source: "expires_at" },
  ]);

  if (explicitExpiry) {
    return {
      dueAt: toIsoString(explicitExpiry.value),
      isExpiryFallback: false,
      expirySource: explicitExpiry.source,
    };
  }

  const uploadDate = firstDefined(row?.upload_date, detail.upload_date);
  const fallbackExpiry = addMonthsClamped(uploadDate, 18);
  return {
    dueAt: fallbackExpiry ? fallbackExpiry.toISOString() : null,
    isExpiryFallback: Boolean(fallbackExpiry),
    expirySource: fallbackExpiry ? "upload_date+18_months" : null,
  };
}

function buildOriginalDownloadActions(photos, now) {
  return normalizeRows(photos).flatMap((row, index) => {
    const detail = row?.detail || {};
    const downloadableCount = firstFinite(
      row?.new_download_file_count,
      detail.new_download_file_count,
      row?.download_file_count,
      detail.download_file_count,
    );
    const isDeleted = Boolean(firstDefined(row?.org_delete_dtm, detail.org_delete_dtm));
    const isCompleted = Boolean(firstDefined(
      row?.download_completed_at,
      detail.download_completed_at,
      row?.download_at,
      detail.download_at,
    ));

    if (downloadableCount <= 0 || isDeleted || isCompleted) return [];

    const scheduleId = firstDefined(row?.schedule_id, detail.schedule_id);
    const photoUploadId = firstDefined(row?.photo_upload_id, detail.photo_upload_id);
    const stableId = firstDefined(photoUploadId, scheduleId, index);
    const { dueAt, isExpiryFallback, expirySource } = resolveOriginalDownloadExpiry(row);
    const fileSize = firstFinite(
      row?.file_size,
      row?.total_file_size,
      detail.file_size,
      detail.total_file_size,
    );
    const formattedSize = formatFileSize(fileSize);
    const expiryDateLabel = formatActionDate(dueAt);
    const downloadPath = appendQuery("/mypage/photos?from=action-center", {
      scheduleId,
    });

    return [withActionTiming({
      id: `original-download:${stableId}`,
      type: CUSTOMER_ACTION_TYPES.ORIGINAL_DOWNLOAD,
      title: "원본사진을 내려받아 주세요",
      description: isExpiryFallback
        ? "현재 보관 종료일은 업로드일 기준 예상값이에요. 늦지 않게 내려받아 주세요."
        : "보관 종료 후에는 원본을 복구하기 어려워요.",
      dueAt,
      expiresAt: dueAt,
      fileSizeLabel: formattedSize,
      deadlineLabel: expiryDateLabel ? `${expiryDateLabel} 보관 종료` : "보관 기간을 확인해 주세요",
      cta: {
        label: "다운로드",
        path: downloadPath,
      },
      progress: {
        current: 0,
        required: downloadableCount,
        unit: "개 파일",
        label: `${downloadableCount}개 파일 다운로드 가능`,
      },
      source: row,
      sourceKind: "api",
      isExpiryFallback,
      expirySource,
      canSnooze: true,
    }, now)];
  });
}

function buildThemeSelectionActions(schedules, now) {
  return normalizeRows(schedules).flatMap((row, index) => {
    const detail = row?.detail || {};
    const buttonType = Number(firstDefined(row?.btn_type, detail.btn_type));
    const availableThemeCount = firstFinite(row?.thema_len, detail.thema_len);
    const statusContents = firstDefined(row?.status_contents, detail.status_contents);
    const scheduleDate = startOfDay(firstDefined(row?.schedule_date, detail.schedule_date));
    const today = startOfDay(now);
    if (buttonType !== 0 || availableThemeCount <= 0) return [];
    if (statusContents !== undefined && statusContents !== null && Number(statusContents) !== 0) return [];
    if (scheduleDate && today && scheduleDate.getTime() < today.getTime()) return [];

    const scheduleId = firstDefined(row?.schedule_id, detail.schedule_id, index);
    const selectedThemes = firstNonEmptyArray(
      row?.selected_theme_list,
      detail.selected_theme_list,
      row?.thema_list_name,
      detail.thema_list_name,
    ).filter((theme) => {
      if (typeof theme === "string") return theme.trim().length > 0;
      return Boolean(theme);
    });
    const selectedCount = firstFinite(
      row?.selected_theme_count,
      detail.selected_theme_count,
      selectedThemes.length,
    );
    const requiredCount = firstFinite(
      row?.required_theme_count,
      detail.required_theme_count,
      row?.theme_max,
      detail.theme_max,
      row?.thema_max,
      detail.thema_max,
      availableThemeCount,
    );
    const dueValue = firstDefined(
      row?.theme_due_at,
      detail.theme_due_at,
      row?.selection_due_at,
      detail.selection_due_at,
      row?.due_at,
      detail.due_at,
      row?.schedule_date,
      detail.schedule_date,
    );
    const dueAt = toIsoString(dueValue);
    const isDemoSchedule = ["theme-needed", "upcoming-selected"].includes(String(scheduleId));
    const progressDescription = requiredCount > 0
      ? `${requiredCount}개 중 ${Math.min(selectedCount, requiredCount)}개 선택`
      : "선택할 테마를 확인해 주세요";

    return [withActionTiming({
      id: `theme-selection:${scheduleId}`,
      type: CUSTOMER_ACTION_TYPES.THEME_SELECTION,
      title: "테마를 선택해 주세요",
      description: "촬영 전에 필요한 테마를 모두 골라 주세요.",
      dueAt,
      deadlineLabel: dueAt ? `${formatActionDate(dueAt)}까지` : "",
      cta: {
        label: selectedCount > 0 ? "계속 선택" : "테마 선택",
        path: isDemoSchedule
          ? appendQuery("/tabs/themes", { selectSchedule: scheduleId, category: "upcoming", from: "action-center" })
          : appendQuery("/selectTheme", { schedule_id: scheduleId, from: "action-center" }),
      },
      progress: {
        current: selectedCount,
        required: requiredCount,
        unit: "개",
        label: progressDescription,
      },
      source: row,
      sourceKind: "api",
      isExpiryFallback: false,
      canSnooze: true,
    }, now)];
  });
}

function buildProductPhotoSelectionActions(products, now) {
  return normalizeRows(products).flatMap((row, index) => {
    const statusCode = String(firstDefined(row?.status_cd, row?.status, ""));
    if (statusCode !== "2") return [];

    const productId = firstDefined(row?.contract_product_id, row?.product_id, index);
    const albumSaveId = firstDefined(row?.album_save_id);
    const photoUploadId = firstDefined(row?.photo_upload_id);
    const selectedPhotos = firstNonEmptyArray(row?.selected_photo_list, row?.selected_photos, row?.photos);
    const selectedCount = firstFinite(row?.selected_photo_count, row?.photo_selected_count, selectedPhotos.length);
    const requiredCount = firstFinite(
      row?.required_photo_count,
      row?.photo_required_count,
      row?.selection_count,
      row?.total_photo_count,
    );
    const dueAt = toIsoString(firstDefined(
      row?.photo_selection_due_at,
      row?.selection_due_at,
      row?.due_at,
      row?.product_due_at,
    ));
    const progressDescription = requiredCount > 0
      ? `${requiredCount}장 중 ${Math.min(selectedCount, requiredCount)}장 선택`
      : "제작에 사용할 사진을 선택해 주세요";

    return [withActionTiming({
      id: `product-photo-selection:${productId}`,
      type: CUSTOMER_ACTION_TYPES.PRODUCT_PHOTO_SELECTION,
      title: "제품 제작 사진을 선택해 주세요",
      description: "제품 제작을 시작하려면 사용할 사진을 골라 주세요.",
      dueAt,
      deadlineLabel: dueAt ? `${formatActionDate(dueAt)}까지` : "",
      cta: {
        label: selectedCount > 0 ? "계속 선택" : "사진 선택",
        path: appendQuery("/productRequestEdit", {
          contract_product_id: productId,
          album_save_id: albumSaveId,
          photo_upload_id: photoUploadId,
          from: "action-center",
        }),
      },
      progress: {
        current: selectedCount,
        required: requiredCount,
        unit: "장",
        label: progressDescription,
      },
      source: row,
      sourceKind: "api",
      isExpiryFallback: false,
      canSnooze: true,
    }, now)];
  });
}

export function sortCustomerActionItems(items) {
  return [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const priorityDifference = (left.priorityRank ?? 3) - (right.priorityRank ?? 3);
    if (priorityDifference !== 0) return priorityDifference;

    const leftDueAt = parseDate(left.dueAt)?.getTime() ?? Number.POSITIVE_INFINITY;
    const rightDueAt = parseDate(right.dueAt)?.getTime() ?? Number.POSITIVE_INFINITY;
    if (leftDueAt !== rightDueAt) return leftDueAt - rightDueAt;

    const typeDifference = (ACTION_TYPE_SORT_ORDER[left.type] ?? 99) - (ACTION_TYPE_SORT_ORDER[right.type] ?? 99);
    if (typeDifference !== 0) return typeDifference;
    return String(left.id).localeCompare(String(right.id));
  });
}

export function createPreviewActionItems({ now = new Date() } = {}) {
  const originalDueAt = addDays(now, 3).toISOString();
  const themeDueAt = addDays(now, 7).toISOString();
  const productDueAt = addDays(now, 14).toISOString();

  return sortCustomerActionItems([
    withActionTiming({
      id: "preview:original-download",
      type: CUSTOMER_ACTION_TYPES.ORIGINAL_DOWNLOAD,
      title: "원본사진을 내려받아 주세요",
      description: "보관 종료 후에는 원본을 복구하기 어려워요.",
      dueAt: originalDueAt,
      expiresAt: originalDueAt,
      fileSizeLabel: "8.4GB",
      deadlineLabel: `${formatActionDate(originalDueAt)} 보관 종료`,
      cta: { label: "다운로드", path: "/tabs/schedules?screen=download&from=action-center&actionPreview=1" },
      progress: { current: 0, required: 1, unit: "개 파일", label: "원본 파일 다운로드 가능" },
      source: null,
      sourceKind: "preview",
      isExpiryFallback: false,
      expirySource: "preview",
      canSnooze: true,
    }, now),
    withActionTiming({
      id: "preview:theme-selection",
      type: CUSTOMER_ACTION_TYPES.THEME_SELECTION,
      title: "테마를 선택해 주세요",
      description: "촬영 전에 필요한 테마를 모두 골라 주세요.",
      dueAt: themeDueAt,
      deadlineLabel: `${formatActionDate(themeDueAt)}까지`,
      cta: { label: "계속 선택", path: "/tabs/themes?selectSchedule=theme-needed&category=upcoming&from=action-center&actionPreview=1" },
      progress: { current: 2, required: 4, unit: "개", label: "4개 중 2개 선택" },
      source: null,
      sourceKind: "preview",
      isExpiryFallback: false,
      canSnooze: true,
    }, now),
    withActionTiming({
      id: "preview:product-photo-selection",
      type: CUSTOMER_ACTION_TYPES.PRODUCT_PHOTO_SELECTION,
      title: "앨범 사진을 선택해 주세요",
      description: "제품 제작을 시작하려면 사용할 사진을 골라 주세요.",
      dueAt: productDueAt,
      deadlineLabel: `${formatActionDate(productDueAt)}까지`,
      cta: { label: "사진 선택", path: "/tabs/products?tab1=1&from=action-center&actionPreview=1" },
      progress: { current: 8, required: 20, unit: "장", label: "20장 중 8장 선택" },
      source: null,
      sourceKind: "preview",
      isExpiryFallback: false,
      canSnooze: true,
    }, now),
  ]);
}

/**
 * Builds actionable cards from the existing schedule, product and photo responses.
 * `fallbackToPreview` is intentionally opt-in so an empty server response never
 * invents work for a real customer. It is useful for the LOCAL demo screen only.
 */
export function buildCustomerActionItems({
  schedules = [],
  products = [],
  photos = [],
  now = new Date(),
  fallbackToPreview = false,
} = {}) {
  const actionItems = sortCustomerActionItems([
    ...buildOriginalDownloadActions(photos, now),
    ...buildThemeSelectionActions(schedules, now),
    ...buildProductPhotoSelectionActions(products, now),
  ]);

  if (actionItems.length === 0 && fallbackToPreview) {
    return createPreviewActionItems({ now });
  }

  return actionItems;
}

function getDefaultStorage() {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

export function createCustomerActionSnoozeStorageKey(customerId) {
  const scopedCustomerId = customerId === undefined || customerId === null || customerId === ""
    ? "guest"
    : String(customerId);
  return `${CUSTOMER_ACTION_SNOOZE_STORAGE_PREFIX}:${encodeURIComponent(scopedCustomerId)}`;
}

export function createCustomerActionCountStorageKey(customerId) {
  const scopedCustomerId = customerId === undefined || customerId === null || customerId === ""
    ? "guest"
    : String(customerId);
  return `${CUSTOMER_ACTION_COUNT_STORAGE_PREFIX}:${encodeURIComponent(scopedCustomerId)}`;
}

export function getCachedCustomerActionCount({ customerId, storage } = {}) {
  const targetStorage = storage || (typeof window === "undefined" ? null : window.sessionStorage);
  if (!targetStorage) return 0;
  const value = Number(targetStorage.getItem(createCustomerActionCountStorageKey(customerId)) || 0);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function cacheCustomerActionCount({ customerId, count, storage } = {}) {
  const targetStorage = storage || (typeof window === "undefined" ? null : window.sessionStorage);
  if (!targetStorage) return 0;
  const safeCount = Math.max(0, Math.floor(Number(count) || 0));
  targetStorage.setItem(createCustomerActionCountStorageKey(customerId), String(safeCount));
  return safeCount;
}

export function decrementCachedCustomerActionCount({ customerId, storage } = {}) {
  const targetStorage = storage || (typeof window === "undefined" ? null : window.sessionStorage);
  if (!targetStorage) return null;
  const storageKey = createCustomerActionCountStorageKey(customerId);
  if (targetStorage.getItem(storageKey) === null) return null;

  return cacheCustomerActionCount({
    customerId,
    count: Math.max(0, getCachedCustomerActionCount({ customerId, storage: targetStorage }) - 1),
    storage: targetStorage,
  });
}

export function createCustomerActionCompletedStorageKey(customerId) {
  const scopedCustomerId = customerId === undefined || customerId === null || customerId === ""
    ? "guest"
    : String(customerId);
  return `${CUSTOMER_ACTION_COMPLETED_STORAGE_PREFIX}:${encodeURIComponent(scopedCustomerId)}`;
}

function readCompletedActionRecord(customerId, storage) {
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(createCustomerActionCompletedStorageKey(customerId)) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
}

export function markCustomerActionCompleted({ customerId, actionId, storage, completedAt = new Date() } = {}) {
  const targetStorage = storage || (typeof window === "undefined" ? null : window.localStorage);
  if (!targetStorage || !actionId) return false;
  const record = readCompletedActionRecord(customerId, targetStorage);
  if (record[String(actionId)]) return false;
  record[String(actionId)] = toIsoString(completedAt) || new Date().toISOString();
  targetStorage.setItem(createCustomerActionCompletedStorageKey(customerId), JSON.stringify(record));
  return true;
}

export function filterCompletedCustomerActionItems(items, { customerId, storage } = {}) {
  const targetStorage = storage || (typeof window === "undefined" ? null : window.localStorage);
  const record = readCompletedActionRecord(customerId, targetStorage);
  return (Array.isArray(items) ? items : []).filter((item) => !record[String(item.id)]);
}

function readSnoozeRecord(customerId, storage = getDefaultStorage()) {
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(createCustomerActionSnoozeStorageKey(customerId)) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function writeSnoozeRecord(customerId, record, storage = getDefaultStorage()) {
  if (!storage) return;
  const key = createCustomerActionSnoozeStorageKey(customerId);
  if (Object.keys(record).length === 0) {
    storage.removeItem(key);
    return;
  }
  storage.setItem(key, JSON.stringify(record));
}

export function loadCustomerActionSnoozes({ customerId, storage = getDefaultStorage(), now = new Date() } = {}) {
  const record = readSnoozeRecord(customerId, storage);
  const nowTime = parseDate(now)?.getTime() ?? Date.now();

  return Object.fromEntries(Object.entries(record).filter(([, until]) => {
    const snoozedUntil = parseDate(until);
    return snoozedUntil && snoozedUntil.getTime() > nowTime;
  }));
}

export function snoozeActionItem({
  customerId,
  actionId,
  until,
  storage = getDefaultStorage(),
  now = new Date(),
} = {}) {
  if (!actionId) return false;
  const nowDate = parseDate(now) || new Date();
  const snoozedUntil = parseDate(until) || new Date(nowDate.getTime() + DEFAULT_SNOOZE_MS);
  if (snoozedUntil.getTime() <= nowDate.getTime()) return false;

  const record = loadCustomerActionSnoozes({ customerId, storage, now: nowDate });
  record[String(actionId)] = snoozedUntil.toISOString();
  writeSnoozeRecord(customerId, record, storage);
  return true;
}

export function clearActionItemSnooze({ customerId, actionId, storage = getDefaultStorage() } = {}) {
  if (!actionId) return;
  const record = readSnoozeRecord(customerId, storage);
  delete record[String(actionId)];
  writeSnoozeRecord(customerId, record, storage);
}

export function filterSnoozedActionItems(items, {
  customerId,
  storage = getDefaultStorage(),
  now = new Date(),
} = {}) {
  const snoozes = loadCustomerActionSnoozes({ customerId, storage, now });
  return (Array.isArray(items) ? items : []).filter((item) => !snoozes[String(item.id)]);
}
